import type { FC, ReactNode } from 'react'
import type { Office } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useClock } from '@/hooks/use-clock'

import { MENU_ITEMS, OFFICES } from '@/constants/navigation'

interface SiteMenuProps {
    id: string
    open: boolean
    onClose: () => void
}

interface OfficeRowProps {
    office: Office
}

const OfficeRow: FC<OfficeRowProps> = ({ office }): ReactNode => {
    const clock = useClock(office.timeZone)

    return (
        <div className='grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 border-t border-hairline py-5'>
            <p className='hud text-ink'>
                {office.city}
            </p>

            <p className='hud text-ink' suppressHydrationWarning>
                {clock.text}
            </p>

            <p className='hud text-mute'>
                {office.coords}
            </p>

            <p className='hud text-mute' suppressHydrationWarning>
                {clock.offset}
            </p>
        </div>
    )
}

export const SiteMenu: FC<SiteMenuProps> = ({ id, open, onClose }): ReactNode => {
    const router = useRouter()
    const panelRef = useRef<HTMLDivElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (!open) return

        const handleRoute = (): void => onClose()

        router.events.on('routeChangeStart', handleRoute)

        return () => {
            router.events.off('routeChangeStart', handleRoute)
        }
    }, [open, onClose, router.events])

    useEffect(() => {
        if (!open) return

        const previousOverflow = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        closeRef.current?.focus()

        const handleKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                event.preventDefault()
                onClose()

                return
            }

            if (event.key !== 'Tab' || !panelRef.current) return

            const focusables = panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')

            if (focusables.length === 0) return

            const first = focusables[0]
            const last = focusables[focusables.length - 1]

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', handleKey)

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', handleKey)
        }
    }, [open, onClose])

    const isActive = (href: string): boolean => {
        if (href === '/') return router.pathname === '/'

        return router.pathname === href || router.pathname.startsWith(`${href}/`)
    }

    return (
        <div
            id={id}
            ref={panelRef}
            role='dialog'
            aria-modal='true'
            aria-label='Site menu'
            aria-hidden={!open}
            inert={!open}
            className={cn(
                'fixed inset-0 z-50 flex flex-col overflow-y-auto bg-background transition-[opacity,visibility] duration-500 ease-instrument',
                open ? 'visible opacity-100' : 'invisible opacity-0'
            )}
        >
            <div className='flex h-14 shrink-0 items-center justify-between border-b border-hairline px-gutter desk:h-16'>
                <div className='flex items-center gap-6'>
                    <span className='font-heading text-[15px] font-semibold tracking-[0.2em] text-ink'>
                        NORRVIK
                    </span>

                    <span className='hud hidden text-mute sm:inline'>
                        Index / Navigation
                    </span>
                </div>

                <button
                    ref={closeRef}
                    type='button'
                    onClick={onClose}
                    className='hud group inline-flex h-9 items-center gap-3 border border-ink/40 pl-4 pr-3 text-ink transition-colors duration-300 hover:border-ink'
                >
                    Close
                    <X className='size-4' strokeWidth={1.5} aria-hidden='true' />
                </button>
            </div>

            <div className='grid flex-1 gap-12 px-gutter py-8 desk:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] desk:gap-16 desk:py-12'>
                <nav aria-label='Site'>
                    <ul className='border-b border-hairline'>
                        {MENU_ITEMS.map((item, index) => {
                            const active = isActive(item.href)

                            return (
                                <li
                                    key={item.href}
                                    className={cn(
                                        'border-t border-hairline transition-[opacity,transform] duration-700 ease-instrument',
                                        open ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
                                    )}
                                    style={{ transitionDelay: open ? `${80 + index * 50}ms` : '0ms' }}
                                >
                                    <Link
                                        href={item.href}
                                        onClick={() => {
                                            if (active) onClose()
                                        }}
                                        aria-current={active ? 'page' : undefined}
                                        className='group flex items-baseline gap-5 py-2.5 desk:py-3'
                                    >
                                        <span className={cn('hud w-8 shrink-0', active ? 'text-signal' : 'text-mute')}>
                                            {item.index}
                                        </span>

                                        <span
                                            className={cn(
                                                'font-heading text-[clamp(28px,3.6vw,48px)] font-medium leading-none tracking-[-0.03em] transition-colors duration-300',
                                                active ? 'text-ink' : 'text-dim group-hover:text-ink'
                                            )}
                                        >
                                            {item.label}
                                        </span>
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <div className='flex flex-col justify-between gap-10'>
                    <div>
                        <p className='hud mb-4 text-mute'>
                            Offices / Local time
                        </p>

                        {OFFICES.map(office => (
                            <OfficeRow key={office.code} office={office} />
                        ))}
                    </div>

                    <div className='border-t border-hairline pt-5'>
                        <p className='hud text-mute'>
                            Architecture and building intelligence
                        </p>

                        <p className='hud text-mute'>
                            Survey / Design / Monitor / Archive
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}
